import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../api/client';

export interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  phone_verified?: boolean;
  status?: string;
  role?: string;
  profileImage?: string | null;
  created_at?: string;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  workspaceId: string;
  loading: boolean;
  error: string | null;
  verificationPhone: string | null;
  otpCode: string | null;
}

const initialState: AuthState = {
  user: null,
  token: api.getToken(),
  workspaceId: api.getWorkspaceId(),
  loading: false,
  error: null,
  verificationPhone: null,
  otpCode: null,
};

export const restoreSession = createAsyncThunk(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    try {
      const token = api.getToken();
      if (!token) return null;
      const res = await api.user.getProfile();
      if (res.success && res.data) {
        return { user: res.data, token };
      }
      api.setToken(null);
      return null;
    } catch (err: any) {
      api.setToken(null);
      return rejectWithValue(err.message || 'Session expired');
    }
  }
);

export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const res = await api.auth.login(email, password);
      if (res.success && res.data) {
        return res.data;
      }
      return rejectWithValue(res.message || 'Login failed');
    } catch (err: any) {
      return rejectWithValue(err.message || 'Login failed');
    }
  }
);

export const signupUser = createAsyncThunk(
  'auth/signup',
  async ({ name, email, phone, password }: { name: string; email: string; phone: string; password: string }, { rejectWithValue }) => {
    try {
      const res = await api.auth.signup(name, email, phone, password);
      if (res.success && res.data) {
        return { phone, otpCode: res.data.otpCode };
      }
      return rejectWithValue(res.message || 'Signup failed');
    } catch (err: any) {
      return rejectWithValue(err.message || 'Signup failed');
    }
  }
);

export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async ({ phone, code }: { phone: string; code: string }, { rejectWithValue }) => {
    try {
      const res = await api.auth.verifyOtp(phone, code);
      if (res.success && res.data) {
        return res.data;
      }
      return rejectWithValue(res.message || 'Verification failed');
    } catch (err: any) {
      return rejectWithValue(err.message || 'Verification failed');
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateProfile',
  async (data: { name?: string; password?: string; profileImage?: string | null }, { rejectWithValue }) => {
    try {
      const res = await api.user.updateProfile(data);
      if (res.success && res.data) {
        return res.data;
      }
      return rejectWithValue(res.message || 'Update profile failed');
    } catch (err: any) {
      return rejectWithValue(err.message || 'Update profile failed');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    await api.auth.logout();
    return null;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setWorkspace(state, action: PayloadAction<string>) {
      state.workspaceId = action.payload;
      api.setWorkspaceId(action.payload);
    },
    clearError(state) {
      state.error = null;
    },
    clearOtpCode(state) {
      state.otpCode = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // restoreSession
      .addCase(restoreSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.user = action.payload.user;
          state.token = action.payload.token;
        } else {
          state.user = null;
          state.token = null;
        }
      })
      .addCase(restoreSession.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
      })
      // login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // signup
      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.loading = false;
        state.verificationPhone = action.payload.phone;
        state.otpCode = action.payload.otpCode;
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // verifyOtp
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.verificationPhone = null;
        state.otpCode = null;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // updateProfile
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
      });
  }
});

export const { setWorkspace, clearError, clearOtpCode } = authSlice.actions;
export default authSlice.reducer;
